import React, { useMemo } from 'react';
import { moneyFormat } from 'design-system';
import {
  CoverageCatalog,
  QuotationUnit,
  CoverageCatalogResume,
} from 'data-layer-cr/dist/models';
import {
  coverageForCode,
  getDeductibleCoveragesForUnit,
} from 'data-layer-cr/dist/functions/quotations';
import { deductibleCoverages } from 'data-layer-cr/dist/functions/coverages';
import InformationRow from './InformationRow';

type Props = {
  coverageCatalog: CoverageCatalog[];
  coverageCatalogResumes: CoverageCatalogResume[];
  unit: QuotationUnit;
  currency: string;
};

type DeductibleCoverage = {
  code: string;
  description: string;
  value: string;
};

const { formatMoney } = moneyFormat;

const DeductibleInformation = ({
  coverageCatalog,
  coverageCatalogResumes,
  unit,
  currency,
}: Props): JSX.Element => {
  const quotationCoverageData = useMemo(
    () => getDeductibleCoveragesForUnit(unit, coverageCatalogResumes),
    [unit, coverageCatalogResumes],
  );

  const informationResume = useMemo(
    () =>
      deductibleCoverages(coverageCatalog).map(
        (coverage): DeductibleCoverage => {
          const dataFromQuotation = coverageForCode(
            quotationCoverageData,
            coverage.code,
          );
          const deductibleValue = dataFromQuotation
            ? dataFromQuotation.deductible
            : 0;

          return {
            code: coverage.code,
            description: coverage.name,
            value: formatMoney(deductibleValue, false, currency),
          };
        },
      ),
    [coverageCatalog, quotationCoverageData, currency],
  );

  return (
    <>
      {informationResume.map(({ code, description, value }) => (
        <InformationRow
          data-testid={`deductible-${code}`}
          key={code}
          description={description}
          value={value}
        />
      ))}
    </>
  );
};

export default DeductibleInformation;
